<template>

<com-section :section="currentFormAsSection" :back="isFirstForm ? undefined : true" v-on:back="onFormBack" v-on:next="onFormNext">

	<slot></slot>

	<template v-for="formSection in formSections">

		<com-form :section="formSection" :key="formSection.id" v-if="formSection.id === currentForm && !currentAlert" />

	</template>

</com-section>

</template>

<script>

import comSection from '@/components/tool/common/Section'
import comForm from '@/components/tool/common/section/Form'

export default {

	props: ['section'],

	components: {
		'com-section': comSection,
		'com-form': comForm
	},

	data: function() {

		return {
			currentForm: 0,
			postAlertPending: false
		}

	},

	watch: {

		// When a post-form alert is dismissed (Continue), resume advancing.
		currentAlert: function(now, was) {

			if (this.postAlertPending && was && !now) {

				this.postAlertPending = false
				this.advanceForm()

			}

		}

	},

	created: function() {

		if (this.formSections.length) {
			var first = this.$_.findIndex(this.formSections, this.hasVisibleInputs.bind(this))
			this.currentForm = this.formSections[first !== -1 ? first : 0].id
		}

	},

	computed: {

		formSections: function() {

			return this.$store.getters['data/form/sections/patient']

		},

		currentFormAsSection: function() {

			var form = this.$_.findWhere(this.formSections, { id: this.currentForm })

			return form ? {
				id: this.section.id,
				type: this.section.type,
				name: this.section.name,
				icon: this.section.icon,
				info: this.section.info,
				text: form.text,
				patient: this.section.patient
			} : this.section

		},

		currentFormIndex: function() {

			return this.$_.findIndex(this.formSections, { id: this.currentForm })

		},

		isFirstForm: function() {

			return this.currentFormIndex === 0

		},

		isLastForm: function() {

			return this.currentFormIndex === this.formSections.length - 1

		}

	},

	methods: {

		onFormNext: function() {

			var formSection = this.$_.findWhere(this.formSections, { id: this.currentForm })

			if (!formSection) return

			// Validate required fields
			this.$store.commit('session/inputs/flagged/reset')

			var invalid = false
			var self = this

			this.$_.each(formSection.layout, function(layout) {

				if (layout.type === self.$constants.layout.type.text) return

				self.$_.each(layout.inputs, function(inputId) {

					var input = self.$store.getters['data/form/inputs'][inputId]

					if (!input) return

					if (!self.$store.getters['logic/visible'](input)) return

					if (self.$store.getters['logic/required'](input) && self.$store.getters['session/values'][input.variable] === null) {
						self.$store.commit('session/inputs/flagged', input.id)
						invalid = true
					}

				})

			})

			if (invalid) return

			// Show any alert attached to this form section before moving on
			// (e.g. the post data-entry message). advanceForm resumes once the
			// alert is dismissed, via the currentAlert watcher.
			if (this.showFormAlert(formSection)) return

			this.advanceForm()

		},

		showFormAlert: function(formSection) {

			if (!formSection || !formSection.alerts || !formSection.alerts.length) return false

			var alerts = this.$store.getters['data/form/alerts']
			var completed = this.$store.getters['session/alerts/completed']
			var self = this
			var match = null

			// Patient mode: always show the form's alert (e.g. the post
			// data-entry message), even if it has been seen before. Clinician
			// build keeps the normal show-once behaviour.
			this.$_.each(formSection.alerts, function(alertId) {

				if (match) return

				var alert = alerts[alertId]

				if (alert && self.$store.getters['logic/visible'](alert) && (self.$isPatient || !self.$_.contains(completed, alert.id))) {
					match = alert
				}

			})

			if (!match) return false

			this.postAlertPending = true
			this.$store.commit('session/alerts/current', match.id)

			return true

		},

		advanceForm: function() {

			var nextIndex = this.currentFormIndex + 1

			while (nextIndex < this.formSections.length && !this.hasVisibleInputs(this.formSections[nextIndex])) {
				nextIndex++
			}

			if (nextIndex >= this.formSections.length) {
				this.mapPatientValues()
				this.$emit('next')
			} else {
				this.currentForm = this.formSections[nextIndex].id
			}

		},

		onFormBack: function() {

			var prevIndex = this.currentFormIndex - 1

			while (prevIndex >= 0 && !this.hasVisibleInputs(this.formSections[prevIndex])) {
				prevIndex--
			}

			if (prevIndex < 0) {
				this.$emit('back')
			} else {
				this.currentForm = this.formSections[prevIndex].id
			}

		},

		hasVisibleInputs: function(formSection) {

			var self = this

			return this.$_.some(formSection.layout, function(layout) {

				if (layout.type === self.$constants.layout.type.text) return true

				return self.$_.some(layout.inputs, function(inputId) {
					var input = self.$store.getters['data/form/inputs'][inputId]
					return input && self.$store.getters['logic/visible'](input)
				})

			})

		},

		mapPatientValues: function() {

			var named = this.$store.getters['logic/variable/named']
			var values = this.$store.getters['session/values']

			var setByRef = function(ref, value) {
				var id = named[ref]
				if (id !== undefined && value !== null && value !== undefined) {
					this.$store.commit('session/values/override', { id: id, value: value })
				}
			}.bind(this)

			var getByRef = function(ref) {
				var id = named[ref]
				return (id !== undefined) ? values[id] : null
			}

			setByRef('ref.age', getByRef('patient.age'))

			var sex = getByRef('patient.sex')
			if (sex !== null) {
				this.$store.commit('session/values/override', { id: 5, value: sex })
			}

			var fraxMajor = getByRef('patient.frax_major')
			var fraxHip = getByRef('patient.frax_hip')

			if (fraxMajor !== null) {
				setByRef('ref.mof', fraxMajor)
				setByRef('system.1a', parseFloat(fraxMajor))
			}

			if (fraxHip !== null) {
				setByRef('ref.hip', fraxHip)
				setByRef('system.1b', parseFloat(fraxHip))
			}

			var tscore = getByRef('patient.tscore')

			if (tscore !== null) {
				setByRef('ref.lowest', tscore)
			}

		}

	}

}

</script>

<style scoped>

</style>
